import React, { useState, useEffect } from "react";
import I18n from "../../../../../i18n/utils";
import { useDispatch, useSelector } from "react-redux";
import "./BusinessUnitOverrideForm.scss";
import {
  Card,
  Button,
  Col,
  Row,
  InputGroup,
  FormGroup,
  Label,
  Input,
  CardBody,
  CardFooter,
} from "reactstrap";
import CFASpinner from "../../../Common/CFASpinnner/CFASpinner";
import {
  formatReverseDate,
  formatDateWithFullYearUtc,
  isOverlappingDateRange,
} from "../../../../utils/DateUtil";

import {
  deliveryPartnerBusinessUnitUpdateSelector,
  updateBusinessUnitOverrides,
  deleteBusinessUnitOverride,
} from "../../../../redux/features/deliveryPartnerSlice";
import closeOutline from "../../../../assets/icons/close-outline.svg";
import deliveryCar from "../../../../assets/icons/delivery-car.svg";
import successIcon from "../../../../assets/icons/success-icon.svg";
import IBusinessUnitOverrideForm from "../../../../interfaces/IBusinessUnitOverrideForm";
import DeliveryPartnerBusinessUnitOverride from "../../../../interfaces/DeliveryPartnerBusinessUnitOverride";
import { getTimestamp } from "../../../../utils/DateUtil";
import { getOktaUser } from "../../../../utils/LocalStorage";
import { Typeahead, TypeaheadMenu } from "react-bootstrap-typeahead";
import {
  FAILED,
  IDLE,
  NEEDS_REFRESH,
} from "../../../../redux/features/constants";
import { fetchBusinessUnits } from "../../../../redux/features/businessSlice";
import { RootState } from "../../../../redux/store/store";

const BusinessUnitOverrideForm: React.FC<IBusinessUnitOverrideForm> = ({
  businessUnitOverrideForm,
  selectedOverride,
  close,
  businessUnits,
}: IBusinessUnitOverrideForm): JSX.Element => {
  const [form, setForm] = useState<DeliveryPartnerBusinessUnitOverride>({
    deliveryPartnerId: selectedOverride.deliveryPartnerId,
    locationNumber: selectedOverride.locationNumber
      ? [selectedOverride.locationNumber]
      : [],
    deliveryPartnerBusinessUnitOverrideId:
      selectedOverride.deliveryPartnerBusinessUnitOverrideId,
    //@ts-ignore
    commissionRate: selectedOverride.commissionRate
      ? selectedOverride.commissionRate
      : 0,
    overrideCommissionFee: 0,
    startTimestamp: selectedOverride.startTimestamp
      ? selectedOverride.startTimestamp
      : formatReverseDate(Date.now()),
    // @ts-ignore
    endTimestamp: selectedOverride.endTimestamp
      ? selectedOverride.endTimestamp
      : null,
    lastUpdatedBy: selectedOverride.lastUpdatedBy,
    createdBy: selectedOverride.createdBy,
    lastUpdatedTimestamp: selectedOverride.lastUpdatedTimestamp,
    objectVersionNumber: selectedOverride.objectVersionNumber,
    creationTimestamp: selectedOverride.creationTimestamp,
  });
  const [formTitle, setFormTitle] = useState("");
  const [editingOverride, setEditingOverride] = useState(false);
  const dispatch = useDispatch();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const overridesUpdateError = useSelector(
    (state: RootState) => state.deliveryPartners.overrideUpdateStatus
  );
  const businessUnitsReduxStatus = useSelector(
    (state: RootState) => state.businessUnits.status
  );
  const businessUnitOverridesForPartner = useSelector(
    deliveryPartnerBusinessUnitUpdateSelector
  );
  const oktaUserInfo = getOktaUser();

  useEffect(() => {
    if (
      businessUnitsReduxStatus === IDLE ||
      businessUnitsReduxStatus === NEEDS_REFRESH
    ) {
      dispatch(fetchBusinessUnits());
    }
    if (overridesUpdateError === FAILED) {
      setError(true);
    }

    // Determine if adding new partner & commission, adding commission, or editing commission
    // Initial var setup based on props
    // FIXME: pass props from parents more uniformly to simplify logic
    let _editingOverride =
      selectedOverride.deliveryPartnerBusinessUnitOverrideId == null
        ? false
        : true;
    const locationNumber = selectedOverride.locationNumber
      ? [selectedOverride.locationNumber]
      : [];
    setFormTitle(
      selectedOverride.deliveryPartnerBusinessUnitOverrideId == null
        ? I18n.t("APP_BUSINESS_UNIT_OVERRIDES_ADD_FORM_TITLE")
        : I18n.t("APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_TITLE")
    );

    if (!_editingOverride) {
      setForm({
        ...form,
        locationNumber,
        objectVersionNumber: 0,
      });
    }
    setEditingOverride(_editingOverride);

    // NOTE: do not use exhaustive hooks for the "form" variable dependency, since it causes an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitOverrideForm, editingOverride, selectedOverride]);

  async function handleChangeInput(
    event: { target: { name: any; value: any } } | null
  ) {
    if (event == null) return;

    let name = event.target.name;
    let value = event.target.value;

    if (name === "startTimestamp" || name === "endTimestamp") {
      value = formatReverseDate(value);
      if (value === "Invalid Date") {
        value = null;
      }
    }

    const currentForm = form;

    const _form = {
      ...currentForm,
      [name]: value,
    };

    await setForm(_form);
  }

  function isSubmitDisabled() {
    let bool = false;

    const {
      deliveryPartnerId,
      locationNumber,
      commissionRate,
      startTimestamp,
    } = form;

    if (
      deliveryPartnerId === "" ||
      // @ts-ignore
      locationNumber === "" ||
      locationNumber.length === 0 ||
      commissionRate === "" ||
      startTimestamp === ""
    ) {
      bool = true;
    }

    return bool;
  }

  // 3 possible cases: editing commission, adding commission, or adding delivery partner and commission
  function submitForm() {
    let currentForm = { ...form };
    // check that there is only one null end date for this location and no overlapping date ranges
    // TODO: modularize this and commission rate history form submit
    const overridesForPartner: any =
      businessUnitOverridesForPartner.deliveryPartnerBusinessUnitOverrides;
    for (let i = 0; i < overridesForPartner.length; i++) {
      const override = overridesForPartner[i];
      // if editing, only send alert if override with null endTimestamp  at that location is not the current override
      if (editingOverride) {
        if (
          override.deliveryPartnerBusinessUnitOverrideId !==
          currentForm.deliveryPartnerBusinessUnitOverrideId
        ) {
          // TODO: refactor locationNumber into two variables - array version used for typeahead and string version
          // to eliminate need for indexing
          if (
            override.locationNumber === currentForm.locationNumber[0] &&
            override.endTimestamp === null &&
            currentForm.endTimestamp === null
          ) {
            // TODO: i18n
            alert(
              "Error: Cannot have two active overrides at the same location for this delivery partner." +
                ` Please add an end date to override at location ${override.locationNumber}`
            );
            return;
          }
          if (
            override.locationNumber === currentForm.locationNumber[0] &&
            isOverlappingDateRange(
              currentForm.startTimestamp,
              currentForm.endTimestamp,
              override.startTimestamp,
              override.endTimestamp
            )
          ) {
            alert(
              "Error: Overrides cannot have overlapping start and/or end dates."
            );
            return;
          }
        }
        // if adding, send alert if any override has a null endtimestamp at the same locationNumber that user is trying to add
      } else {
        if (
          override.locationNumber === currentForm.locationNumber[0] &&
          override.endTimestamp === null &&
          currentForm.endTimestamp === null
        ) {
          alert(
            "Error: Cannot have two active location overrides at the same location for this delivery partner." +
              ` Please add an end date to override at location ${override.locationNumber}`
          );
          return;
        }
        if (
          override.locationNumber === currentForm.locationNumber[0] &&
          isOverlappingDateRange(
            currentForm.startTimestamp,
            currentForm.endTimestamp,
            override.startTimestamp,
            override.endTimestamp
          )
        ) {
          alert(
            "Error: Overrides cannot have overlapping start and/or end dates."
          );
          return;
        }
      }
    }

    // Add createdBy and lastUpdatedBy values to current user name if empty
    if (
      currentForm["createdBy"] === "" ||
      currentForm["createdBy"] === undefined
    )
      currentForm["createdBy"] = oktaUserInfo[0];
    currentForm["lastUpdatedBy"] = oktaUserInfo[0];
    // Turn timestamp mills to ISO timestamp
    if (currentForm["startTimestamp"])
      currentForm["startTimestamp"] = getTimestamp(form.startTimestamp);
    // Extract location number from typeahead array
    currentForm.locationNumber = currentForm.locationNumber[0];

    // @ts-ignore
    dispatch(updateBusinessUnitOverrides([currentForm]));
  }

  function submitDeleteCommission() {
    if (window.confirm("Delete Business Unit Override?")) {
      const { deliveryPartnerBusinessUnitOverrideId } = selectedOverride;
      dispatch(
        // @ts-ignore
        deleteBusinessUnitOverride(deliveryPartnerBusinessUnitOverrideId)
      );
    }
  }

  function handleClearTimestamp(isStartTimestamp: boolean) {
    let name = isStartTimestamp ? "startTimestamp" : "endTimestamp";
    //Clears the display value within the Input[date] element
    //@ts-ignore
    document.getElementById(name).value = "";

    const currentForm = form;
    const _form = {
      ...currentForm,
      [name]: null,
    };
    setForm(_form);
  }

  function handleBusinessUnitSelectionChange(selection: any) {
    const event = {
      target: {
        name: "locationNumber",
        value: !selection || !selection.length ? [] : selection,
      },
    };
    handleChangeInput(event);
  }

  return (
    <>
      <Col className="BusinessUnitOverrideForm container">
        {!businessUnitOverridesForPartner.message ? (
          <>
            <Row>
              <Col className="container">
                <Card className="dashboard-card">
                  <CardBody>
                    <Row>
                      <FormGroup className="update-partner-form">
                        <Row className="close-button-container">
                          <img
                            alt=""
                            src={closeOutline}
                            className="close-modal-icon"
                            onClick={close}
                          />
                        </Row>
                        <Row className="container">
                          <Col className="update-partner-title-container">
                            <img
                              alt=""
                              src={deliveryCar}
                              className="update-partner-title-icon"
                            />
                            <h2 className="update-partner-title">
                              {formTitle}
                            </h2>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm="6" xs="12">
                            {/* PartnerID Input form if it needs to be displayed */}
                            {/* Partner ID */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_PARTNER_ID_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_PARTNER_ID_PLACEHOLDER"
                                  )}
                                  id="deliveryPartnerId"
                                  name="deliveryPartnerId"
                                  value={form.deliveryPartnerId}
                                  type="text"
                                  maxLength={55}
                                  // Use to disable id on edit
                                  disabled={editingOverride ? true : false}
                                  onChange={handleChangeInput}
                                />
                              </InputGroup>
                            </Row>

                            {/* Commission Rate */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_COMMISSION_RATE_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_COMMISSION_RATE_PLACEHOLDER"
                                  )}
                                  id="commissionRate"
                                  name="commissionRate"
                                  value={form.commissionRate}
                                  type="text"
                                  maxLength={55}
                                  disabled={editingOverride ? true : false}
                                  onChange={handleChangeInput}
                                />
                              </InputGroup>
                            </Row>

                            {/* Started Date [Timestamp] */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_START_TIMESTAMP_LABEL"
                                )}
                                :{" "}
                                {form.startTimestamp
                                  ? formatDateWithFullYearUtc(
                                      form.startTimestamp
                                    )
                                  : "N/A"}
                              </Label>
                              <Button
                                className="add-override-modal-button"
                                onClick={() => handleClearTimestamp(true)}
                                disabled={editingOverride ? true : false}
                              >
                                {I18n.t(
                                  "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_CLEAR_TIMESTAMP_BUTTON"
                                )}
                              </Button>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_START_TIMESTAMP_PLACEHOLDER"
                                  )}
                                  id="startTimestamp"
                                  name="startTimestamp"
                                  type="date"
                                  maxLength={55}
                                  disabled={editingOverride ? true : false}
                                  onChange={handleChangeInput}
                                />
                              </InputGroup>
                            </Row>
                          </Col>

                          <Col sm="6" xs="12">
                            {/* Business Unit Number */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_LOCATION_NUMBER_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                {businessUnits && businessUnits.length && (
                                  <>
                                    <Typeahead
                                      id="business-unit-lookup-input"
                                      placeholder={I18n.t(
                                        "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_LOCATION_PLACEHOLDER"
                                      )}
                                      selected={form.locationNumber}
                                      className="business-unit-input"
                                      labelKey="locationNumber"
                                      paginate={true}
                                      caseSensitive={true}
                                      shouldSelect={false}
                                      disabled={editingOverride ? true : false}
                                      onChange={
                                        handleBusinessUnitSelectionChange
                                      }
                                      options={businessUnits}
                                      renderMenu={(
                                        results: string | any[],
                                        menuProps: JSX.IntrinsicAttributes,
                                        props: { text: any }
                                      ) => {
                                        // Hide the menu when there are no results.
                                        if (!results.length) {
                                          return null;
                                        }
                                        return (
                                          <TypeaheadMenu
                                            labelKey={"locationNumber"}
                                            {...menuProps}
                                            options={results}
                                            text={props.text}
                                            defaultInputValue={""}
                                          />
                                        );
                                      }}
                                    />
                                    {form.locationNumber &&
                                      form.locationNumber.length > 5 && (
                                        <p className="selection-error">
                                          {I18n.t(
                                            "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_LOCATION_LIMIT"
                                          )}
                                        </p>
                                      )}
                                  </>
                                )}
                              </InputGroup>
                            </Row>

                            {/* Commission Fee */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_COMMISSION_FEE_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_COMMISSION_FEE_PLACEHOLDER"
                                  )}
                                  id="overrideCommissionFee"
                                  name="overrideCommissionFee"
                                  value={form.overrideCommissionFee || ""}
                                  type="text"
                                  maxLength={55}
                                  disabled={editingOverride ? true : false}
                                  onChange={handleChangeInput}
                                />
                              </InputGroup>
                            </Row>

                            {/* End Date [Timestamp] -- This is optional */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_END_TIMESTAMP_LABEL"
                                )}
                                :{" "}
                                {form.endTimestamp
                                  ? formatDateWithFullYearUtc(form.endTimestamp)
                                  : "N/A"}
                              </Label>
                              <Button
                                className="add-override-modal-button"
                                onClick={() => handleClearTimestamp(false)}
                              >
                                {I18n.t(
                                  "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_CLEAR_TIMESTAMP_BUTTON"
                                )}
                              </Button>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_END_TIMESTAMP_PLACEHOLDER"
                                  )}
                                  id="endTimestamp"
                                  name="endTimestamp"
                                  type="date"
                                  maxLength={55}
                                  onChange={handleChangeInput}
                                />
                              </InputGroup>
                            </Row>
                          </Col>
                        </Row>
                      </FormGroup>
                    </Row>
                    <>
                      {overridesUpdateError === FAILED && (
                        <Row>
                          <h4 className="error">
                            {I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_ERROR")}
                          </h4>
                        </Row>
                      )}
                      <CardFooter>
                        {!loading ? (
                          <>
                            {editingOverride && selectedOverride && (
                              <Button
                                className="delete-button"
                                block
                                onClick={submitDeleteCommission}
                              >
                                {I18n.t(
                                  "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_DELETE"
                                )}
                              </Button>
                            )}
                            <Button
                              disabled={isSubmitDisabled()}
                              className="submit-button"
                              block
                              onClick={submitForm}
                            >
                              {I18n.t(
                                "APP_DELIVERY_PARTNERS_UPDATE_FORM_SUBMIT"
                              )}
                            </Button>
                          </>
                        ) : (
                          <Row className="spinner-container">
                            <CFASpinner />
                          </Row>
                        )}
                      </CardFooter>
                    </>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <Row>
            <Col className="success-container">
              <img alt="" src={successIcon} className="success-icon" />
              <h4>{I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_SUCCESS")}</h4>
              <Button className="go-back-button" block onClick={close}>
                {I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_CLOSE")}
              </Button>
            </Col>
          </Row>
        )}
      </Col>
    </>
  );
};

export default BusinessUnitOverrideForm;
